import {
    BodyChunk,
    HttpMethod,
    HttpStatusCode,
    type MutableHttpHeaders, type ReadonlyHeaderValue,
    ReadonlyHttpHeaders
} from '../../../types/web/http';
import { ClientHttpRequest, ClientHttpResponse } from '../../../types/web/client';

import { AbstractClientHttpRequest } from '../../client/exchange.ts';
import { AbstractHttpResponse } from '../../http/exchange.ts';
import { MapHttpHeaders } from '../../http/headers.ts';
import { httpStatusCode } from '../../http/status.ts';
import * as stream from 'node:stream/web';

export class MockClientHttpRequest extends AbstractClientHttpRequest implements ClientHttpRequest {
    #writeHandler: (body: ReadableStream<BodyChunk>) => Promise<boolean>;
    #body: () => ReadableStream<BodyChunk> = () => {
        throw new Error("Body not set");
    };
    constructor(uri: URL, method: HttpMethod) {
        super(method, uri);
        this.#writeHandler = async (body) => {
            const chunks: BodyChunk[] = [];
            this.#body = () => {
                return stream.ReadableStream.from(chunks) as ReadableStream<BodyChunk>;
            };
            const reader = body.getReader();
            let done: boolean;
            do {
                const {value, done: doneReading} = await reader.read();
                done = doneReading;
                if (value !== undefined) {
                    chunks.push(value);
                }
            } while (!done);
            return true;
        }
    }
    set writeHandler(handler: (body: ReadableStream<BodyChunk>) => Promise<boolean>) {
        this.#writeHandler = handler;
    }

    getNativeRequest<T>(): T {
        return this as unknown as T;
    }

    getBody(): ReadableStream<BodyChunk> {
        return this.#body();
    }

    body(body: ReadableStream<BodyChunk> | Promise<BodyChunk | void> | BodyChunk): Promise<boolean> {
        const asyncIterator = (async function* () {
            if (body instanceof ReadableStream) {
                const reader = body.getReader();
                let done: boolean;
                do {
                    const {value, done: doneReading} = await reader.read();
                    done = doneReading;
                    if (value !== undefined) {
                        yield value;
                    }
                } while (!done);
            }
            else {
                const value = await body;
                if (value !== undefined) {
                    yield value;
                }
            }

        })();
        return this.#writeHandler(stream.ReadableStream.from(asyncIterator) as ReadableStream<BodyChunk>);
    }

    async end() {
        return await this.body(Promise.resolve());
    }

}

export class MockClientHttpResponse extends AbstractHttpResponse<ReadonlyHttpHeaders> implements ClientHttpResponse {
    #statusCode: HttpStatusCode;
    #body: ReadableStream<BodyChunk> = stream.ReadableStream.from([]) as ReadableStream<BodyChunk>;
    constructor(status: number | HttpStatusCode) {
        super(new MapHttpHeaders());
        this.#statusCode = httpStatusCode(status);
    }

    get statusCode(): HttpStatusCode {
        return this.#statusCode;
    }

    addHeader(name: string, value: string | (readonly string[])): this {
        (this.headers as MutableHttpHeaders).add(name, value);
        return this;
    }

    setHeader(name: string, value: ReadonlyHeaderValue): this {
        (this.headers as MutableHttpHeaders).set(name, value);
        return this;
    }

    setBody(body: string, charset?: BufferEncoding): this {
        this.#body = stream.ReadableStream.from([Buffer.from(body, charset)]) as ReadableStream<BodyChunk>;
        return this;
    }

    set body(body: ReadableStream<BodyChunk>) {
        this.#body = body;
    }

    get body() {
        return this.#body;
    }

    async blob() {
        const chunks: BodyChunk[] = [];
        if (this.body !== undefined) {
            for await (const chunk of this.body) {
                chunks.push(chunk);
            }
        }
        return new Blob(chunks, {type: this.headers.one('content-type') as string || 'application/octet-stream'});
    }
}
